import { formatAcraAddress, toProperAddressCase, type AcraEntityRecord } from './dataGovSg';
import { getAcraEntityByUen, searchAcraEntitiesByNameTokens, searchAcraEntitiesFromDb } from './acraEntitiesDb';
import { looksLikeUen } from './acraBucketMap';
import { isUenMarkedNa } from './uenValue';
import type { AccountIntelRegistry } from '../types/crm';

export { toProperAddressCase };

export function coerceAcraStatus(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    const trimmed = String(value).trim();
    return trimmed || null;
  }
  return null;
}

export function normalizeIntelProfileRegistry(raw: unknown): AccountIntelRegistry | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  return {
    uen: typeof row.uen === 'string' ? row.uen.trim().toUpperCase() || null : null,
    legalName: typeof row.legalName === 'string' ? row.legalName : null,
    status: coerceAcraStatus(row.status),
    entityType: typeof row.entityType === 'string' ? row.entityType : null,
    registrationDate: typeof row.registrationDate === 'string' ? row.registrationDate : null,
    registeredAddress:
      typeof row.registeredAddress === 'string' && row.registeredAddress.trim()
        ? toProperAddressCase(row.registeredAddress)
        : null,
    primarySsic: typeof row.primarySsic === 'string' ? row.primarySsic : null,
    paidUpCapital: typeof row.paidUpCapital === 'string' ? row.paidUpCapital : null,
    currency: typeof row.currency === 'string' ? row.currency : null,
    source: typeof row.source === 'string' ? row.source : undefined,
    fetchedAt: typeof row.fetchedAt === 'string' ? row.fetchedAt : undefined,
    error: typeof row.error === 'string' ? row.error : null,
  };
}

export function clientShowsAcraStatus(client: {
  uen?: string | null;
  intel_profile_json?: AccountIntelRegistry | null;
}): boolean {
  if (isUenMarkedNa(client.uen)) return false;
  return Boolean(client.uen?.trim() || accountAcraStatus(client));
}

export function isAcraActiveStatus(status: string): boolean {
  const value = status.toLowerCase();
  if (value.includes('deregister') || value.includes('struck') || value.includes('cancel')) {
    return false;
  }
  return value.includes('live') || value.includes('register');
}

export function acraRegistryFromEntity(record: AcraEntityRecord): AccountIntelRegistry {
  const formatted = formatAcraAddress(record);
  const registrationDate =
    record.registrationDate?.trim() || record.issueDate?.trim() || null;
  const agency = record.issuanceAgency?.trim();
  const source =
    agency && !/acra/i.test(agency)
      ? `data.gov.sg (${agency})`
      : 'data.gov.sg ACRA';
  return {
    uen: record.uen,
    legalName: record.entityName,
    status: coerceAcraStatus(record.status) ?? '',
    entityType: record.entityType,
    registrationDate,
    registeredAddress:
      formatted && formatted !== '—'
        ? toProperAddressCase(formatted)
        : null,
    primarySsic: record.primarySsicCode
      ? `${record.primarySsicCode} ${record.primarySsicDescription}`.trim()
      : null,
    source,
    fetchedAt: new Date().toISOString(),
    error: null,
  };
}

export function formatPaidUpCapital(registry: AccountIntelRegistry | null | undefined): string | null {
  const amount = registry?.paidUpCapital?.trim();
  if (!amount) return null;
  const currency = registry?.currency?.trim();
  const numeric = Number(amount.replace(/,/g, ''));
  const pretty = Number.isFinite(numeric) ? numeric.toLocaleString() : amount;
  return currency ? `${currency} ${pretty}` : pretty;
}

/**
 * Address for reports / tasks / deliveries:
 * prefer manual office address; fall back to ACRA registered address only when empty.
 */
export function resolveAccountDisplayAddress(client: {
  address?: string | null;
  intel_profile_json?: AccountIntelRegistry | null;
} | null | undefined): string {
  const office = client?.address?.trim() ?? '';
  if (office) return office;
  const registered = client?.intel_profile_json?.registeredAddress?.trim() ?? '';
  return registered ? toProperAddressCase(registered) : '';
}

/**
 * When office address is empty and intel has no registered address, fill from ACRA
 * entity lookup so list/detail district can fall back with an ACRA badge.
 */
export function withAcraRegisteredAddressFallback<T extends {
  address?: string | null;
  uen?: string | null;
  intel_profile_json?: AccountIntelRegistry | null;
}>(client: T, registeredAddressByUen: Map<string, string>): T {
  if (client.address?.trim()) return client;
  if (client.intel_profile_json?.registeredAddress?.trim()) return client;
  const uen = client.uen?.trim().toUpperCase() ?? '';
  if (!uen || isUenMarkedNa(uen)) return client;
  const registered = registeredAddressByUen.get(uen)?.trim() ?? '';
  if (!registered || registered === '—') return client;
  return {
    ...client,
    intel_profile_json: {
      ...(client.intel_profile_json ?? {}),
      registeredAddress: toProperAddressCase(registered),
    },
  };
}

export async function searchAcraRegistryOptions(
  query: string,
  limit = 12,
): Promise<AcraEntityRecord[]> {
  const trimmed = query.trim();
  if (trimmed.length < 3) return [];

  if (looksLikeUen(trimmed)) {
    const local = await getAcraEntityByUen(trimmed);
    if (local) return [local];
  }

  // Token search first — handles partial names like "1doc medi".
  const byTokens = await searchAcraEntitiesByNameTokens(trimmed, limit);
  if (byTokens.length) return byTokens;

  const fromDb = await searchAcraEntitiesFromDb({ query: trimmed, limit });
  if (fromDb?.records.length) return fromDb.records;

  return [];
}

export function sanitizeIntelProfileRegistry(
  registry: AccountIntelRegistry | null | undefined,
  uen: string,
): AccountIntelRegistry | null {
  const normalizedUen = uen.trim().toUpperCase();
  if (!registry || !normalizedUen) return null;
  if (isUenMarkedNa(normalizedUen)) return null;
  if ((registry.uen ?? '').trim().toUpperCase() !== normalizedUen) return null;

  return {
    uen: normalizedUen,
    legalName: registry.legalName ?? null,
    status: coerceAcraStatus(registry.status),
    entityType: registry.entityType ?? null,
    registrationDate: registry.registrationDate ?? null,
    registeredAddress:
      typeof registry.registeredAddress === 'string' && registry.registeredAddress.trim()
        ? toProperAddressCase(registry.registeredAddress)
        : null,
    primarySsic: registry.primarySsic ?? null,
    paidUpCapital: registry.paidUpCapital ?? null,
    currency: registry.currency ?? null,
    source: registry.source ?? 'data.gov.sg ACRA',
    fetchedAt: registry.fetchedAt ?? new Date().toISOString(),
    error: null,
  };
}

export async function lookupAcraRegistry(
  companyName: string,
  uen = '',
): Promise<AccountIntelRegistry | null> {
  const normalizedUen = uen.trim().toUpperCase();
  if (normalizedUen) {
    const local = await getAcraEntityByUen(normalizedUen);
    if (local) return acraRegistryFromEntity(local);
  }

  const query = normalizedUen || companyName.trim();
  if (query.length < 2) return null;

  const fromDb = await searchAcraEntitiesFromDb({
    query,
    limit: normalizedUen ? 1 : 5,
  });
  if (!fromDb?.records.length) return null;

  if (normalizedUen) {
    const match = fromDb.records.find(
      (row) => row.uen.trim().toUpperCase() === normalizedUen,
    );
    return acraRegistryFromEntity(match ?? fromDb.records[0]);
  }

  const normalizedName = companyName.trim().toLowerCase();
  const exact = fromDb.records.find(
    (row) => row.entityName.trim().toLowerCase() === normalizedName,
  );
  return acraRegistryFromEntity(exact ?? fromDb.records[0]);
}

export function accountAcraStatus(client: {
  uen?: string | null;
  intel_profile_json?: AccountIntelRegistry | null;
}): string | null {
  return coerceAcraStatus(client.intel_profile_json?.status);
}

export function resolveAcraStatusForClient(
  client: {
    uen?: string | null;
    intel_profile_json?: AccountIntelRegistry | null;
  },
  statusMap?: Map<string, string>,
): string | null {
  const uen = client.uen?.trim().toUpperCase() ?? '';
  if (isUenMarkedNa(uen)) return null;
  if (uen && statusMap?.has(uen)) {
    return statusMap.get(uen) ?? accountAcraStatus(client);
  }
  return accountAcraStatus(client);
}
